let socket = null;
let localStream = null;
let peerConnections = new Map(); // socketId -> RTCPeerConnection
let onAudioStreamReceivedCallback = null;

const rtcConfig = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
    { urls: 'stun:stun3.l.google.com:19302' },
    { urls: 'stun:stun4.l.google.com:19302' },
  ],
};

async function loadSocketIoScript() {
  if (window.io) return window.io;
  
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = '/socket.io/socket.io.js';
    script.onload = () => resolve(window.io);
    script.onerror = () => {
      console.warn('[Socket] Não foi possível carregar /socket.io/socket.io.js dinamicamente.');
      resolve(null);
    };
    document.head.appendChild(script);
  });
}

export async function initSocketConnection(onSessionState, onUserListUpdated, onPresentationStarted, onPresentationStopped, onNewReaction, onUserRaisedHand, onSlideStarted, onStreamFrame, onAudioStreamReceived, onPcmAudioChunk) {
  onAudioStreamReceivedCallback = onAudioStreamReceived;

  const ioFunc = await loadSocketIoScript();
  if (!ioFunc) {
    console.warn('[Socket] Executando sem servidor Socket.io ativo no ambiente local.');
    return null;
  }

  socket = ioFunc(window.location.origin, {
    transports: ['websocket', 'polling'],
  });

  socket.on('connect', () => {
    console.log('[Socket] Conectado ao servidor de sinalização com ID:', socket.id);
  });

  socket.on('session-state', (state) => {
    console.log('[Socket] Estado da sessão recebido:', state);
    if (onSessionState) onSessionState(state);
    if (state.currentPresenter && state.currentPresenter.imageBase64 && onSlideStarted) {
      onSlideStarted({ presenter: state.currentPresenter, imageBase64: state.currentPresenter.imageBase64 });
    }
  });

  socket.on('user-list-updated', (users) => {
    console.log('[Socket] Lista de usuários atualizada na sala:', users);
    if (onUserListUpdated) onUserListUpdated(users);
  });

  socket.on('presentation-started', (data) => {
    console.log('[Socket] Apresentação iniciada na sala:', data);
    if (onPresentationStarted) onPresentationStarted(data);
  });
  
  if (onSlideStarted) {
    socket.on('presentation-slide-started', (data) => {
      onSlideStarted(data);
    });
  }

  if (onStreamFrame) {
    socket.on('stream-frame', (frameData) => {
      onStreamFrame(frameData);
    });
  }

  if (onPcmAudioChunk) {
    socket.on('stream-pcm-audio', (pcmBuffer) => {
      onPcmAudioChunk(pcmBuffer);
    });
  }

  socket.on('presentation-stopped', () => {
    console.log('[Socket] Apresentação encerrada.');
    closeAllPeerConnections();
    if (onPresentationStopped) onPresentationStopped();
  });

  socket.on('new-reaction', onNewReaction);
  socket.on('user-raised-hand', onUserRaisedHand);

  // WebRTC Signal handlers
  socket.on('webrtc-offer', async ({ senderSocketId, offer }) => {
    console.log('[WebRTC] Oferta recebida do apresentador:', senderSocketId);
    const pc = createPeerConnection(senderSocketId, false);
    await pc.setRemoteDescription(new RTCSessionDescription(offer));
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);

    socket.emit('webrtc-answer', {
      targetSocketId: senderSocketId,
      answer,
    });
  });

  socket.on('webrtc-answer', async ({ senderSocketId, answer }) => {
    console.log('[WebRTC] Resposta recebida do espectador:', senderSocketId);
    const pc = peerConnections.get(senderSocketId);
    if (pc) {
      await pc.setRemoteDescription(new RTCSessionDescription(answer));
    }
  });

  socket.on('webrtc-ice-candidate', async ({ senderSocketId, candidate }) => {
    const pc = peerConnections.get(senderSocketId);
    if (pc && candidate) {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (err) {
        console.error('[WebRTC] Erro ao adicionar ICE Candidate:', err);
      }
    }
  });

  return socket;
}

export function registerUserInSocket(user, channelId) {
  if (socket) {
    console.log(`[Socket Client] Registrando usuário ${user ? user.username : ''} na sala [${channelId}]`);
    socket.emit('register-user', { user, channelId });
  }
}

export function sendSlidePresentation(imageBase64, currentUser) {
  if (socket) {
    socket.emit('start-slide-presentation', { imageBase64, user: currentUser });
  }
}

export function sendStreamFrame(frameData) {
  if (socket) {
    socket.emit('stream-frame', frameData);
  }
}

export function sendPcmAudioChunk(pcmBuffer) {
  if (socket) {
    socket.emit('stream-pcm-audio', pcmBuffer);
  }
}

export async function startScreenShare(currentUser, onStreamEnded) {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
    const err = 'O seu navegador não suporta a captura de tela nativa.';
    console.error('[WebRTC]', err);
    throw new Error(err);
  }

  try {
    console.log('[WebRTC] Solicitando captura de tela com áudio do sistema...');

    localStream = await navigator.mediaDevices.getDisplayMedia({
      video: { cursor: 'always' },
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      },
      systemAudio: 'include',
      surfaceSwitching: 'include',
    }).catch(() => {
      console.warn('[WebRTC] Captura de áudio não aceita, fallback para vídeo simples.');
      return navigator.mediaDevices.getDisplayMedia({ video: true });
    });

    if (!localStream || localStream.getVideoTracks().length === 0) {
      throw new Error('Nenhum canal de vídeo foi selecionado.');
    }

    localStream.getVideoTracks()[0].onended = () => {
      console.log('[WebRTC] Compartilhamento finalizado pelo usuário.');
      stopScreenShare();
      if (onStreamEnded) onStreamEnded();
    };

    if (socket) {
      socket.emit('start-presentation', currentUser);
    }
    return localStream;
  } catch (error) {
    console.error('[WebRTC] Erro ao capturar tela:', error);
    throw error;
  }
}

export function stopScreenShare() {
  if (localStream) {
    localStream.getTracks().forEach((track) => track.stop());
    localStream = null;
  }
  closeAllPeerConnections();
  if (socket) {
    socket.emit('stop-presentation');
  }
}

export async function connectToViewer(viewerSocketId) {
  if (!localStream || !socket) return;
  console.log('[WebRTC] Criando oferta WebRTC para o espectador:', viewerSocketId);
  const pc = createPeerConnection(viewerSocketId, true);
  
  localStream.getTracks().forEach((track) => {
    pc.addTrack(track, localStream);
  });

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);

  socket.emit('webrtc-offer', {
    targetSocketId: viewerSocketId,
    offer,
  });
}

function createPeerConnection(targetSocketId, isBroadcaster) {
  const pc = new RTCPeerConnection(rtcConfig);
  peerConnections.set(targetSocketId, pc);

  pc.onicecandidate = (event) => {
    if (event.candidate && socket) {
      socket.emit('webrtc-ice-candidate', {
        targetSocketId,
        candidate: event.candidate,
      });
    }
  };

  if (!isBroadcaster) {
    pc.ontrack = (event) => {
      console.log('[WebRTC] Stream remoto com áudio/vídeo recebido do apresentador!');
      const remoteVideo = document.getElementById('remote-video');
      if (remoteVideo && event.streams[0]) {
        remoteVideo.srcObject = event.streams[0];
        remoteVideo.muted = false;
        remoteVideo.volume = 1.0;
        
        remoteVideo.play().catch((err) => {
          console.warn('[Video Play Remote Unmuted Error]', err);
          remoteVideo.muted = true;
          remoteVideo.play();
        });

        if (onAudioStreamReceivedCallback) {
          onAudioStreamReceivedCallback(event.streams[0]);
        }
      }
    };
  }

  pc.onconnectionstatechange = () => {
    console.log(`[WebRTC] Estado da conexão com ${targetSocketId}:`, pc.connectionState);
    if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
      pc.close();
      peerConnections.delete(targetSocketId);
    }
  };

  return pc;
}

function closeAllPeerConnections() {
  peerConnections.forEach((pc) => pc.close());
  peerConnections.clear();
}

export function sendReaction(emoji) {
  if (socket) {
    socket.emit('send-reaction', emoji);
  }
}

export function raiseHand() {
  if (socket) {
    socket.emit('raise-hand');
  }
}
